import { useEffect, useRef, useContext, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import { AppStateContext } from '../../context/AppStateContext';

import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import './MapPage.css';

const QUEST9_TARGET = { lat: 52.248147, lng: 21.002304 };
const QUEST9_TOLERANCE_M = 5;

function haversineDistance(lat1, lng1, lat2, lng2) {
    const R = 6371000;
    const toRad = d => d * Math.PI / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const MapPage = ({socket, content, visible}) => {
    const {API_URL, currentQuest, teamToken} = useContext(AppStateContext);
    const mapContainerRef = useRef();
    const mapRef = useRef();
    const [mapPoints, setMapPoints] = useState([]);
    const [unlockedQuests, setUnlockedQuests] = useState([]);

    const [markers, setMarkers] = useState([]);
    const [mapReady, setMapReady] = useState(false);
    const [geocoderMessage, setGeocoderMessage] = useState(null);
    const [locationDenied, setLocationDenied] = useState(false);
    const [showLocationHelp, setShowLocationHelp] = useState(false);
    const geocoderRef = useRef();
    const geocoderAddedRef = useRef(false);
    const geocoderMessageTimerRef = useRef(null);
    const locationCheckInFlight = useRef(false);

    const checkLocationPermission = () => {
        if (!navigator.geolocation) {
            setLocationDenied(true);
            return;
        }

        // iOS anuluje starsze żądania przy nakładających się zapytaniach
        // i zwraca wtedy code 1, co dawałoby fałszywy banner
        if (locationCheckInFlight.current) return;
        locationCheckInFlight.current = true;

        navigator.geolocation.getCurrentPosition(
            () => {
                locationCheckInFlight.current = false;
                setLocationDenied(false);
            },
            (err) => {
                locationCheckInFlight.current = false;
                setLocationDenied(!!err && err.code === 1);
            },
            { timeout: 10000, maximumAge: 0 }
        );
    };

    useEffect(() => {
        fetch(`${API_URL}/api/quests`)
            .then(response => response.json())
            .then(data => {
                if(data.length) {
                    let mapPointsArray = [];
                    data.forEach(quest => {
                        if(quest.map_points) {
                            quest.map_points.forEach(point => {
                                mapPointsArray.push({
                                    quest_id: quest.id,
                                    coordinates: point.coordinates,
                                    description: point.description || '',
                                    unlocked: false
                                });
                            }); 
                        }
                    });
                    console.log('mapPointsArray', mapPointsArray);
                    setMapPoints(mapPointsArray);
                    
                }
            })
            .catch(error => console.error('Błąd podczas pobierania wszystkich zagadek', error));

        fetch(`${API_URL}/api/team/unlock-quests?teamToken=${teamToken}`)
            .then(response => response.json())
            .then(data => {
                if(data && data.unlockQuests) {
                    console.log('unlocked quests', data);
                    setUnlockedQuests(data.unlockQuests);
                }
            })
            .catch(error => console.error('Błąd podczas pobierania wszystkich zagadek', error));
    }, []);


    useEffect(() => {
        if (unlockedQuests.length > 0 && mapPoints.length > 0) {
            const updatedMapPoints = mapPoints.map(point => {
                const isUnlocked = unlockedQuests.some(unlockedQuest => 
                    unlockedQuest === point.quest_id
                );

                return isUnlocked 
                    ? { ...point, unlocked: true, style_modifier: currentQuest.id === point.quest_id ? '-active' : '' } 
                    : point;
            });
            console.log('updatedMapPoints', updatedMapPoints)
            setMapPoints(updatedMapPoints);
        }
    }, [unlockedQuests, mapPoints.length]);

    useEffect(() => {
        if(currentQuest) {
            const updatedMapPoints = mapPoints.map(point => {
                const isUnlocked = point.quest_id === currentQuest.id;
                if(isUnlocked) {
                    return { ...point, unlocked: true, style_modifier: '-active' };
                } else {
                    return { ...point, style_modifier: '' };
                }

                // return isUnlocked 
                //     ? { ...point, unlocked: true, style_modifier: currentQuest.id === point.quest_id ? '-active' : '' }
                //     : point;
            });

            console.log('updatedMapPoints 2', updatedMapPoints)
            setMapPoints(updatedMapPoints);

           

        }
    }, [currentQuest]);

    useEffect(() => {
if (!mapReady || !geocoderRef.current) return;
        const isQuest9 = currentQuest && currentQuest.id === 'quest-9';
        if (isQuest9 && !geocoderAddedRef.current) {
            mapRef.current.addControl(geocoderRef.current);
            geocoderAddedRef.current = true;
        } else if (!isQuest9 && geocoderAddedRef.current) {
            mapRef.current.removeControl(geocoderRef.current);
            geocoderAddedRef.current = false;
            setGeocoderMessage(null);
            if (geocoderMessageTimerRef.current) clearTimeout(geocoderMessageTimerRef.current);
        }
    }, [currentQuest, mapReady]);

    useEffect(() => {
        if (!mapReady || !visible) return;
        checkLocationPermission();
    }, [mapReady, visible]);

    useEffect(() => {
        mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

        mapRef.current = new mapboxgl.Map({
            container: mapContainerRef.current,
            style: "mapbox://styles/malchare/cmm6lmo1u00ap01qwbvo4dgat", // styl
            center: [21.00157798710044, 52.24552519170832], // Długa 52 52.24552519170832, 21.00157798710044
            zoom: 17, // starting zoom
            maxBounds: [
                [20.998280941937207, 52.244104430476526], // południowo-zachodni róg (lng, lat)
                [21.007561384701358, 52.25000322625193]  // północno-wschodni róg (lng, lat)
            ]
        });

        const coordinatesGeocoder = function (query) {
            const matches = query.match(
                /^[ ]*(?:Lat: )?(-?\d+\.?\d*)[, ]+(?:Lng: )?(-?\d+\.?\d*)[ ]*$/i
            );
            if (!matches) return null;

            function coordinateFeature(lng, lat) {
                return {
                    center: [lng, lat],
                    geometry: { type: 'Point', coordinates: [lng, lat] },
                    place_name: lat + ', ' + lng,
                    place_type: ['coordinate'],
                    properties: {},
                    type: 'Feature'
                };
            }

            const coord1 = Number(matches[1]);
            const coord2 = Number(matches[2]);
            const geocodes = [];

            //if (coord1 < -90 || coord1 > 90) geocodes.push(coordinateFeature(coord1, coord2));
            if (coord2 < -90 || coord2 > 90) geocodes.push(coordinateFeature(coord2, coord1));
            if (geocodes.length === 0) {
                //geocodes.push(coordinateFeature(coord1, coord2));
                geocodes.push(coordinateFeature(coord2, coord1));
            }
            return geocodes;
        };

        geocoderRef.current = new MapboxGeocoder({
            accessToken: mapboxgl.accessToken,
            localGeocoder: coordinatesGeocoder,
            localGeocoderOnly: true,
            zoom: 17,
            placeholder: 'współrzędne (np. 52.2455, 21.0015)',
            mapboxgl: mapboxgl,
            reverseGeocode: true
        });

        const MAP_BOUNDS = {
            minLng: 20.998280941937207,
            maxLng: 21.007561384701358,
            minLat: 52.244104430476526,
            maxLat: 52.25000322625193
        };

        geocoderRef.current.on('result', (e) => {
            const [lng, lat] = e.result.center;
            const dist = haversineDistance(lat, lng, QUEST9_TARGET.lat, QUEST9_TARGET.lng);
            const isCorrect = dist <= QUEST9_TOLERANCE_M;

            const isWithinBounds =
                lng >= MAP_BOUNDS.minLng && lng <= MAP_BOUNDS.maxLng &&
                lat >= MAP_BOUNDS.minLat && lat <= MAP_BOUNDS.maxLat;

            const message = isCorrect
                ? { text: '✅ Poprawne współrzędne!', ok: true }
                : isWithinBounds
                    ? { text: `❌ Niepoprawne. Odległość: ${Math.round(dist)} m`, ok: false }
                    : { text: '❌ Niepoprawne. Podane współrzędne są poza obszarem mapy.', ok: false };

            setGeocoderMessage(message);
            if (geocoderMessageTimerRef.current) clearTimeout(geocoderMessageTimerRef.current);
            geocoderMessageTimerRef.current = setTimeout(() => setGeocoderMessage(null), 5000);

            // Przesuń mapę tak, żeby punkt był trochę wyżej niż środek ekranu
            setTimeout(() => {
                mapRef.current.easeTo({
                    center: [lng, lat],
                    zoom: 17,
                    offset: [0, -100]
                });
            }, 50);
        });

         // Add geolocate control to the map.
        const geolocate = new mapboxgl.GeolocateControl({
            positionOptions: {
                enableHighAccuracy: true
            },
            trackUserLocation: true,
            showUserHeading: true
        });
        // udany odczyt pozycji = zgoda jest, banner może zniknąć
        geolocate.on('geolocate', () => {
            locationCheckInFlight.current = false;
            setLocationDenied(false);
        });

        geolocate.on('error', (e) => {
            locationCheckInFlight.current = false;
            if (e && e.code === 1) {
                console.log('error geolocate', e);
                setLocationDenied(true);
            }
        });

        mapRef.current.addControl(geolocate);

        const tryGeolocation = () => {
            if (!navigator.geolocation) {
                setLocationDenied(true);
                return;
            }

            if (locationCheckInFlight.current) return;
            locationCheckInFlight.current = true;

            navigator.geolocation.getCurrentPosition(
                () => {
                    locationCheckInFlight.current = false;
                    setLocationDenied(false);
                    geolocate.trigger();
                },
                (err) => {
                    locationCheckInFlight.current = false;

                    if (err && err.code === 1) {
                        setLocationDenied(true);
                        return;
                    }

                    setLocationDenied(false);
                    geolocate.trigger();
                },
                { timeout: 10000, maximumAge: 0 }
            );
        };

        // Automatycznie wywołaj geolokalizację po dodaniu kontrolki
        mapRef.current.on('load', () => {
            if (navigator.permissions && navigator.permissions.query) {
                navigator.permissions.query({ name: 'geolocation' }).then((result) => {
                    const isDenied = result.state === 'denied';
                    setLocationDenied(isDenied);

                    if (!isDenied) {
                        geolocate.trigger();
                    }

                    result.onchange = () => {
                        if (result.state === 'denied') {
                            setLocationDenied(true);
                        } else {
                            setLocationDenied(false);
                            geolocate.trigger();
                        }
                    };
                }).catch(() => {
                    // Safari na iPhone często nie obsługuje Permissions API, więc używamy fallbacku
                    tryGeolocation();
                });
            } else {
                tryGeolocation();
            }
            setMapReady(true);
        });

        return () => {
            mapRef.current.remove();
        };
    }, []);
  
    useEffect(() => {

        if (!mapRef.current) return;

        // usuń stare markery
        //if(markers.length) markers.forEach(m => m.remove());
        if(markers.length) markers.forEach(m => {
            if (m) m.remove(); // Add null check here
        });

         // stwórz nowe
        const newMarkers = mapPoints.map(point => {

            if(point.unlocked === false) return null;

            const el = document.createElement('div');
            el.className = 'custom-marker';
            //el.style.background = point.color || '#e92353';
            if (point.style_modifier) {
                el.classList.add(point.style_modifier);
            }
            if (point.quest_id) {
                el.classList.add(point.quest_id);
            }
            el.addEventListener('click', () => {
                console.log("Kliknięto marker!", point);
            });

            const popupContent = document.createElement('div');
            const h3 = document.createElement('h3');
            h3.textContent = point.description || '';
            popupContent.appendChild(h3);

            return new mapboxgl.Marker(el)
                .setLngLat(point.coordinates)
                .setPopup(new mapboxgl.Popup({ offset: 25 }).setDOMContent(popupContent))
                .addTo(mapRef.current);
        });

        setMarkers(newMarkers);

    }, [mapPoints]);

    return (
        <div
            style={{ width: '100%', height: '100%', position: 'relative' }}
            className={`map-container ${visible ? '-visible' : '-hide'}`}
        >
            <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />
            {geocoderMessage && (
                <div className={`geocoder-result-message${geocoderMessage.ok ? ' -ok' : ' -error'}`}>
                    {geocoderMessage.text}
                </div>
            )}
            {locationDenied && visible && (
                <div className="location-denied-banner">
                    <span className="location-denied-banner__text">
                        📍 Brak dostępu do lokalizacji. Zezwól na lokalizację w ustawieniach i odśwież stronę.
                    </span>
                    <button
                        type="button"
                        className="location-denied-banner__info"
                        aria-label="Jak włączyć lokalizację"
                        onClick={() => setShowLocationHelp(true)}
                    >
                        i
                    </button>
                </div>
            )}

            {showLocationHelp && (
                <div className="location-help-overlay" onClick={() => setShowLocationHelp(false)}>
                    <div className="location-help-modal" onClick={(e) => e.stopPropagation()}>
                        <button
                            type="button"
                            className="location-help-modal__close"
                            aria-label="Zamknij"
                            onClick={() => setShowLocationHelp(false)}
                        >
                            ×
                        </button>

                        <h3>Jak nadać uprawnienia do lokalizacji</h3>

                        <div className="location-help-modal__section">
                            <h4>iPhone</h4>
                            <ol>
                                <li>Otwórz Ustawienia.</li>
                                <li>Wejdź w Prywatność i ochrona.</li>
                                <li>Wejdź w Usługi lokalizacji i włącz je.</li>
                                <li>Znajdź na liście przeglądarkę Safari lub inną której używasz.</li>
                                <li>Ustaw dla przeglądarki uprawnienia.</li>
                            </ol>
                        </div>

                        <div className="location-help-modal__section">
                            <h4>Android</h4>
                            <ol>
                                <li>Otwórz Ustawienia.</li>
                                <li>Wejdź w zakładkę Lokalizacja.</li>
                                <li>Wybierz Dostęp do lokalizacji.</li>
                                <li>Znajdź przeglądarkę i nadaj uprawnienia.</li>
                            </ol>
                        </div>
                    </div>
                </div>
            )}
        </div>
);
};

export default MapPage;